<template>
    <div class="card">
        <!-- Only load once loading is complete -->
        <template v-if="loading">
            <Skeleton class="m-2" height="20rem" borderRadius="16px" />
        </template>
        <template v-else>
            <h5 class="text-center">{{ metricTitle }}</h5>
            <div v-if="chartEnabled" class="flex justify-content-end mb-2">
                <ToggleButton v-model="tableMode" onIcon="pi pi-table" onLabel="Table Mode" offIcon="pi pi-chart-line" offLabel="Chart Mode" invalid class="w-full sm:w-10rem" aria-label="Confirmation" />
            </div>
            <template v-if="tableMode">
                <StaticTable :jsonData="metricData" :metricTitle="metricTitle" :userCountFlag="metric.userCountFlag" />
            </template>
            <template v-else>
                <StaticChart :jsonData="metricData" :chartType="chartType" />
            </template>
        </template>
    </div>
</template>

<script setup>
import StaticTable from './StaticTable.vue';
import StaticChart from './StaticChart.vue';

import { onMounted, defineProps, ref, computed } from 'vue';

import { useStaticDashboardStore } from '../store/staticDashboard';

// Props
const props = defineProps({
    metric: {
        type: Object,
        required: true,
    },
});

const store = useStaticDashboardStore();

// States
const loading = ref(false);
const tableMode = ref(true); // by default, active mode is set to table

const chartType = ref(null);
const metricData = ref(null);
const metricTitle = ref(null);

const chartEnabled = computed(() => {
    return props.metric.chart;
});

onMounted(() => {
    //console.log('props.metric', props.metric)
    loadMetricData();
});

const loadMetricData = async () => {
    let metricId = null; //It could be id or metric_id

    if (Object.hasOwn(props.metric, 'metric_id')) {
        metricId = props.metric.metric_id;
    } else if (Object.hasOwn(props.metric, 'id')) {
        metricId = props.metric.metric_id;
    } else {
        metricId = props.metric;
    }


    try {
        loading.value = true;
        // extract data from api
        const jsonData = await store.getMetricDataById(metricId);
        // Extract chart type from jsonData
        // If it doesn't exist, then check type directly or else put table by default to avoid errors
        chartType.value = jsonData?.metric?.type ?? props.metric.type ?? 'table';

        /*
        If the type is table, the following error will be thrown:
        Uncaught (in promise) Error: "table" is not a registered controller.

        Defaulting to `bar` in that case
         */
        if (chartType.value === 'table') {
            chartType.value = 'bar';
        }

        metricTitle.value = jsonData?.metric?.name ?? 'Metric Title';

        metricData.value = convertMetricToFormat(jsonData);
    } catch (error) {
        console.error('Error while loading metric id:', metricId);
        //console.log(error);
    } finally {
        loading.value = false;
    }
};

// At the moment it supports only one grouping data at a time
// Multiple groups data not available for testing yet
const convertMetricToFormat = (jsonData) => {
    let convertedMetricData = [];
    // Extract the series first
    const allSeries = jsonData?.metric?.series?.map((seriesObj) => seriesObj.label);

    // Extract the series data
    const seriesData = jsonData?.series?.data;
    // Now loop through the series and map all the data to each series accordingly
    // Using for loop to avoid incorrect order of mappings
    for (let i = 0; i < allSeries?.length; i++) {
        const currentSeriesLabel = allSeries[i]; // Use this as series main label

        const currentSeriesData = seriesData[currentSeriesLabel]?.details?.result; // loop through this to get the data of current series

        const newSeriesObj = {
            label: currentSeriesLabel,
            values: [],
        };
        newSeriesObj.values = currentSeriesData.map((obj) => {
            // Get the dynamic property name which is different than answer

            /*
            Backed team comment:
            The `dynamicKey` should be bound with the procedure to read it.
            It isn't just a random string where the API puts the result...
            By memory, in the case of multiple `groups-by` it could be nested...
             */

            const dynamicKey = Object.keys(obj).find((key) => key !== 'answer');

            let label = currentSeriesLabel;

            if (dynamicKey !== undefined) {

                if (jsonData?.series?.labels !== undefined && Object.hasOwn(jsonData?.series?.labels, dynamicKey)) {

                    const originalLabel = obj[dynamicKey].toString();

                    label = originalLabel;

                    if (Object.hasOwn(jsonData?.series?.labels[dynamicKey], originalLabel)) {
                        label = jsonData?.series?.labels[dynamicKey]?.[originalLabel];
                    }
                }
            }

            // Create the transformed object
            return {
                label: label,
                value: obj.answer,
            };
        });

        convertedMetricData.push(newSeriesObj);
    }
    // Now transpose the series and grouped by respectively
    const transposedArray = convertedMetricData.reduce((result, obj) => {
        obj.values.forEach((item) => {
            const existingSeries = result.find((series) => series.label === item.label);
            if (existingSeries) {
                existingSeries.values.push({ label: obj.label, value: item.value });
            } else {
                result.push({ label: item.label, values: [{ label: obj.label, value: item.value }] });
            }
        });
        return result;
    }, []);
    // console.log(transposedArray);
    return transposedArray;
};
</script>
